/**
 * Per-app delivery readiness (plan 2026-08-15-001 U5, KTD4).
 *
 * Readiness is **computed on every read**, never stored — a stored column
 * drifts the moment a registry field is edited. It is reported **per
 * capability**, never as a single boolean: the fleet's actual state today is
 * "can build, cannot read store review state," which one flag cannot express.
 *
 * Four statuses, deliberately distinct:
 *  - `ready`          — every input this capability needs is present and usable.
 *  - `blocked`        — the platform supports this capability, but this app is
 *                       missing named inputs. Actionable by an operator.
 *  - `unsupported`    — the platform itself does not implement this capability
 *                       yet (the deferred Google Play submission lane, KD4). Its
 *                       inputs are still named so the gap stays honest: it is
 *                       neither "ready" nor "this app's fault".
 *  - `not-applicable` — this capability cannot apply to this app at all, and no
 *                       operator action would change that. The School Journey
 *                       app has no school (`school_id IS NULL`), so it can never
 *                       hold a school-scoped provisioning credential; reporting
 *                       that as `blocked` would put a permanent unfixable gap on
 *                       an operator's list.
 */

import { z } from 'zod'

import { MobileAppPushChannelSchema } from './mobile-app-push-channel'
import { SchoolAppSetupCompletenessSchema } from './school-app-setup'

/**
 * The capability vocabulary. Closed union so the UI can group and filter
 * without string matching.
 */
export const APP_READINESS_CAPABILITIES = [
  'build-ios',
  'build-android',
  'submit-ios',
  'submit-android',
  'ota-update',
  // `push` is being RETIRED (push-readiness-platform-split plan R3/U8) and is
  // kept here only until every reader has moved to the two keys below. It reads
  // an app's OneSignal registry columns and nothing else, so it reports `ready`
  // for an app that holds no APNs key and no Android transport — four
  // production schools are exactly that today (SCH-1512). Do not add a reader.
  'push',
  // The two channels fail INDEPENDENTLY and are therefore two capabilities
  // (KTD1): the APNs key and the Android transport live in OneSignal, not in
  // our registry, and an app can hold one and not the other. Each pairs the
  // registry preconditions every send needs (`onesignal_app_id`,
  // `onesignal_api_key`) with that platform's channel credential as a stored
  // snapshot reports it. A snapshot that is absent, stale or unreadable
  // resolves `blocked`/`unreadable` — "not checked", never ready (R8).
  'push-ios',
  'push-android',
  'store-api-reads',
  'runtime-branding'
] as const

export const AppReadinessCapabilitySchema = z.enum(APP_READINESS_CAPABILITIES)
export type AppReadinessCapability = z.infer<typeof AppReadinessCapabilitySchema>

export const AppReadinessStatusSchema = z.enum(['ready', 'blocked', 'unsupported', 'not-applicable'])
export type AppReadinessStatus = z.infer<typeof AppReadinessStatusSchema>

/**
 * Why an input does not count as present.
 *
 * `placeholder` is separate from `missing` on purpose: `expo_project_id` of
 * `your-expo-project-id-here` and `onesignal_app_id` of `""` are both real
 * rows in the registry today, and reporting either as "present" is exactly
 * the failure mode this resolver exists to prevent.
 *
 * `unreadable` marks a row whose shape could not be read at all — readiness
 * degrades for that one app rather than failing the whole fleet response.
 */
export const AppReadinessMissingReasonSchema = z.enum(['missing', 'placeholder', 'invalid', 'unreadable'])
export type AppReadinessMissingReason = z.infer<typeof AppReadinessMissingReasonSchema>

/**
 * Values that are syntactically present but semantically absent. Treating any
 * of these as a real value is the failure readiness exists to prevent: the
 * registry has held `expo_project_id = 'your-expo-project-id-here'` and
 * `onesignal_app_id = ''` on real rows, and a judge that reports those apps
 * ready is worse than one that reports nothing.
 *
 * ONE RULE (SCH-2963). Capability readiness, the build gate, build-config
 * eligibility, the setup preflight and the web edit form all call this, so
 * "missing" means the same thing on every surface. Do not add a second set of
 * patterns anywhere else; `tests/static/readiness-single-judge.test.ts` fails
 * when one appears.
 */
const PLACEHOLDER_TOKENS = /^(todo|tbd|tba|fixme|xxx|null|undefined|none|n\/a|na|placeholder|changeme|unset)$/i
const PLACEHOLDER_SCAFFOLD = /^your[-_].*[-_]here$/i

/** Why a stored value does not count as present, or null when it does. Never echoes the value. */
export function classifyFieldValue(value: unknown): AppReadinessMissingReason | null {
  if (value === null || value === undefined) return 'missing'
  if (typeof value !== 'string') return 'invalid'
  const trimmed = value.trim()
  if (trimmed.length === 0) return 'missing'
  if (PLACEHOLDER_TOKENS.test(trimmed) || PLACEHOLDER_SCAFFOLD.test(trimmed)) return 'placeholder'
  if (trimmed.includes('placeholder')) return 'placeholder'
  return null
}

/**
 * The closed set of registry-row identities a missing input can name: the
 * `MobileApp` columns readiness reads, the presence-only `onesignal_api_key` and
 * `datadog_client_token`, and `mobile_app` for a row that could not be read at all.
 *
 * WHY A TUPLE AND NOT JUST STRINGS. The web checklist joins on these names, and
 * the backend resolver types its field → column map against them, so renaming
 * or removing one breaks typecheck on both sides instead of silently leaving a
 * checklist row that never matches.
 */
export const READINESS_REGISTRY_COLUMNS = [
  'name',
  'display_name',
  'school_app_id',
  'bundle_id',
  'android_package',
  'expo_slug',
  'expo_project_id',
  'splash_background_color',
  'apple_team_id',
  'asc_app_id',
  'google_project_id',
  'onesignal_app_id',
  'build_profiles',
  'firebase_ios_app_id',
  'firebase_android_app_id',
  'datadog_rum_app_id',
  'onesignal_api_key',
  'datadog_client_token',
  'mobile_app'
] as const
export type ReadinessRegistryColumn = (typeof READINESS_REGISTRY_COLUMNS)[number]

/** Vault identities, `vault:<provider>` — a credential row, not a registry column. */
export type ReadinessVaultColumn = `vault:${'apple-asc' | 'google-play'}`

/** Channel identities, `push_channel:<platform>` — a OneSignal check, not a registry column. */
export type ReadinessChannelColumn = `push_channel:${'ios' | 'android'}`

/**
 * Every identity the resolver may emit as a missing input's `column`. Only
 * `asset_slot:` stays open: slot names are an open-ended vocabulary owned by
 * the asset manifest, not a fixed list readiness could enumerate.
 */
export type ReadinessColumn =
  | ReadinessRegistryColumn
  | ReadinessVaultColumn
  | ReadinessChannelColumn
  | `capability:${AppReadinessCapability}`
  | `asset_slot:${string}`

const READINESS_REGISTRY_COLUMN_SET: ReadonlySet<string> = new Set(READINESS_REGISTRY_COLUMNS)

export function isReadinessRegistryColumn(value: string): value is ReadinessRegistryColumn {
  return READINESS_REGISTRY_COLUMN_SET.has(value)
}

export const AppReadinessMissingFieldSchema = z.object({
  /**
   * The resolver's own name for the input: a Prisma field (`expoProjectId`), a
   * presence key (`ascApiKey`, `apnsKey`), an asset slot name, or a marker
   * (`registryRow`, `capability`). Diagnostic only — join on `column`.
   */
  field: z.string(),
  /**
   * The input's identity, one of {@link ReadinessColumn}: a registry column
   * (`expo_project_id`) or a `<store>:<key>` name (`vault:apple-asc`,
   * `asset_slot:logo-small-light`).
   *
   * Deliberately `z.string()`, not a closed enum: the backend (ECS) and web
   * (Vercel) deploy separately, and an older web bundle would fail to parse
   * the WHOLE app response the moment the backend added a column.
   */
  column: z.string(),
  reason: AppReadinessMissingReasonSchema
})
export type AppReadinessMissingField = z.infer<typeof AppReadinessMissingFieldSchema>

export const AppReadinessCapabilityResultSchema = z.object({
  capability: AppReadinessCapabilitySchema,
  status: AppReadinessStatusSchema,
  /** Empty when `ready`. Populated for `blocked`, `unsupported` and `not-applicable`. */
  missing: z.array(AppReadinessMissingFieldSchema),
  /** One line naming what this app cannot do while the capability is unmet. */
  consequence: z.string()
})
export type AppReadinessCapabilityResult = z.infer<typeof AppReadinessCapabilityResultSchema>

/**
 * Whether Apple can reach us for this app (SCH-1294 R7/R8, KTD3).
 *
 * A SEPARATE axis from the capability list, resolved from STORED state — the
 * latest `webhook.registered` event and the latest `webhookPingCreated`
 * delivery — never from an Apple call on a read. Four states, and `cannot_check`
 * is a fact with a reason, never a pass: an app whose team has no App Store
 * Connect key reads grey, not green (the three prior "cannot check rendered as
 * fine" defects in `docs/plans/fleet-app-delivery/h-fleet-health.md` U29).
 */
export const ASC_WEBHOOK_READINESS_STATES = [
  'registered_pinged',
  'registered_never_pinged',
  'not_registered',
  'cannot_check'
] as const
export const AscWebhookReadinessStateSchema = z.enum(ASC_WEBHOOK_READINESS_STATES)
export type AscWebhookReadinessState = z.infer<typeof AscWebhookReadinessStateSchema>

/**
 * Why the webhook state could not be determined. Each names a different fix:
 * `no_asc_key` → vault the team's key; `keys_dead` → rotate it; `no_apple_team`
 * / `no_asc_app_id` → fill the registry row; `unreadable` → the read itself
 * failed, nothing for an operator to do.
 */
export const AscWebhookCannotCheckReasonSchema = z.enum([
  'no_asc_key',
  'keys_dead',
  'no_apple_team',
  'no_asc_app_id',
  'unreadable'
])
export type AscWebhookCannotCheckReason = z.infer<typeof AscWebhookCannotCheckReasonSchema>

export const AscWebhookReadinessSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('registered_pinged'),
    /** ISO-8601 `receivedAt` of the latest `webhookPingCreated` delivery resolved to this app. */
    lastPingAt: z.string(),
    /** ISO-8601 `occurredAt` of the latest `webhook.registered` event, when one exists. */
    registeredAt: z.string().nullable()
  }),
  z.object({
    state: z.literal('registered_never_pinged'),
    registeredAt: z.string()
  }),
  z.object({ state: z.literal('not_registered') }),
  z.object({ state: z.literal('cannot_check'), reason: AscWebhookCannotCheckReasonSchema })
])
export type AscWebhookReadiness = z.infer<typeof AscWebhookReadinessSchema>

/**
 * Signal freshness — the vendor-neutral answer to "when did the platform last
 * prove it can hear this app's store state?" (vendor-neutral readiness plan,
 * R10-R14/R16, KTD6).
 *
 * A LAYER OVER EXISTING DETAIL, never a replacement for it. Apple's proof
 * arrives as a webhook ping, so an Apple app keeps `AscWebhookReadiness` as its
 * detail. Play has no webhook to register at all; its proof is the daily ledger
 * read, so a Play app keeps that `play` lane's own outcome as its detail. The
 * neutral pair on top — a mode and a freshness — is the only part a fleet
 * rollup can count across both vendors, and it is derived on every read rather
 * than stored, exactly as the capability list above is.
 *
 * PLAY APPS NEVER RECEIVE `registered_*` WORDS. There is nothing to register,
 * and a Play app rendered "not registered" would name an operator step that
 * does not exist.
 */
export const SIGNAL_MODES = ['push', 'poll'] as const
export const SignalModeSchema = z.enum(SIGNAL_MODES)
export type SignalMode = z.infer<typeof SignalModeSchema>

/**
 * Five values, and every distinction between them is one this axis exists to
 * keep (`docs/plans/fleet-app-delivery/h-fleet-health.md` U29):
 *
 *   fresh           proven inside the cadence this mode is judged against
 *   stale           proven, but longer ago than that cadence allows
 *   never           nothing has ever proven it — an absent row is THIS, never
 *                   `fresh` and never `not_applicable`
 *   not_applicable  this app cannot carry this signal at all (R16). Not a gap,
 *                   and nothing an operator does would change it
 *   cannot_check    the check could not run; `detail` carries why. Never a pass,
 *                   and never collapsed into `never`
 */
export const SIGNAL_FRESHNESS_STATES = ['fresh', 'stale', 'never', 'not_applicable', 'cannot_check'] as const
export const SignalFreshnessStateSchema = z.enum(SIGNAL_FRESHNESS_STATES)
export type SignalFreshnessState = z.infer<typeof SignalFreshnessStateSchema>

/**
 * Poll mode's detail: the `play` lane's own last outcome, carried through
 * unchanged so the neutral verdict above always has the vendor fact beneath it.
 *
 * `outcome` is a WIDENED string rather than `DeliveryLaneOutcome` on purpose.
 * `MobileAppDeliveryState.lanes` is an open JSON column a migration or a
 * hand-edit can reach, and a word this reader does not recognise must reach the
 * surface as itself rather than be erased into one it does. The derivation
 * decides any unrecognised word toward `cannot_check`, never toward `fresh`.
 */
export const PollSignalDetailSchema = z.object({
  outcome: z.string(),
  /** Scrubbed provider text. Meaningful when the lane could not be read. */
  reason: z.string().optional()
})
export type PollSignalDetail = z.infer<typeof PollSignalDetailSchema>

export const SignalFreshnessSchema = z.object({
  mode: SignalModeSchema,
  freshness: SignalFreshnessStateSchema,
  /** ISO-8601 instant of the last completed proof; null when nothing has proven it. */
  lastProvenAt: z.string().nullable(),
  /**
   * The cadence in hours the freshness was judged against — the number an
   * operator would move to change the verdict. Reported RAW (24), not multiplied
   * by the staleness tolerance (36): the cadence is the promise, the multiplier
   * is how much slack that promise is given.
   */
  cadenceHours: z.number(),
  /**
   * The vendor fact the verdict was derived from. Null in exactly one case: a
   * Play app whose ledger holds no `play` lane at all, where there is no
   * recorded outcome to carry and `never` is the whole answer.
   */
  detail: z.union([AscWebhookReadinessSchema, PollSignalDetailSchema]).nullable()
})
export type SignalFreshness = z.infer<typeof SignalFreshnessSchema>

/**
 * The account rollup's five counters, one per `SIGNAL_FRESHNESS_STATES` value
 * (R15). Five rather than "covered / not covered" for the same reason the
 * freshness union has five members: `not_applicable` is not a gap, and
 * `cannot_check` is not a pass.
 */
export const SignalCoverageCountsSchema = z.object({
  fresh: z.number().int().nonnegative(),
  stale: z.number().int().nonnegative(),
  never: z.number().int().nonnegative(),
  notApplicable: z.number().int().nonnegative(),
  cannotCheck: z.number().int().nonnegative()
})
export type SignalCoverageCounts = z.infer<typeof SignalCoverageCountsSchema>

/**
 * The registry columns an operator edits in the dashboard's app form, in the form's order
 * (SCH-2963, KTD3). The readiness module reads it to choose `fill-field` (a form row holds the
 * value) over `resolve-capability` (nothing in the form does); the web asserts its checklist rows
 * match it. A column added to the form and not here would be sent to "resolve" instead of "fill".
 */
export const READINESS_EDITABLE_COLUMNS = [
  'name',
  'bundle_id',
  'android_package',
  'school_app_id',
  'expo_project_id',
  'expo_slug',
  'build_profiles',
  'splash_background_color',
  'apple_team_id',
  'asc_app_id',
  'google_project_id',
  'onesignal_app_id'
] as const satisfies readonly ReadinessRegistryColumn[]
export type ReadinessEditableColumn = (typeof READINESS_EDITABLE_COLUMNS)[number]

const READINESS_EDITABLE_COLUMN_SET: ReadonlySet<string> = new Set(READINESS_EDITABLE_COLUMNS)

export function isReadinessEditableColumn(value: string): value is ReadinessEditableColumn {
  return READINESS_EDITABLE_COLUMN_SET.has(value)
}

/**
 * The one next action for an app (SCH-2963, KTD4), judged by the backend readiness module from
 * stored facts and, when it has run, the live build gate. It names a readiness `column` or a gate
 * `checkId` — never a dashboard section: the web maps a column to its form row itself, and may
 * sharpen the action only with facts the server cannot see (an unsaved value, a form-only row).
 *
 * In priority order: `unreadable` (readiness degraded), `fill-field` (a blocked column a form row
 * holds is missing or a placeholder), `resolve-capability` (a blocked input no form row holds),
 * `upload-asset` (a required asset slot), `fix-build-check` (a blocking gate check failed, iOS
 * before Android), `run-build-check` (the gate has not run, or a platform could not be read), `none`.
 */
export const AppReadinessNextActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unreadable') }),
  z.object({
    kind: z.literal('fill-field'),
    column: z.string(),
    capability: AppReadinessCapabilitySchema,
    reason: AppReadinessMissingReasonSchema
  }),
  z.object({ kind: z.literal('resolve-capability'), column: z.string(), capability: AppReadinessCapabilitySchema }),
  z.object({ kind: z.literal('upload-asset'), column: z.string() }),
  z.object({
    kind: z.literal('fix-build-check'),
    platform: z.enum(['IOS', 'ANDROID']),
    checkId: z.string(),
    label: z.string()
  }),
  z.object({ kind: z.literal('run-build-check') }),
  z.object({ kind: z.literal('none') })
])
export type AppReadinessNextAction = z.infer<typeof AppReadinessNextActionSchema>

export const MobileAppReadinessSchema = z.object({
  /** `MobileApp.publicId`. */
  appId: z.string(),
  appName: z.string().nullable(),
  /** App Store Connect webhook coverage. Independent of `capabilities`; see `AscWebhookReadinessSchema`. */
  webhook: AscWebhookReadinessSchema,
  /**
   * The vendor-neutral freshness layer over `webhook` (Apple) or the ledger's
   * `play` lane (Play) — see {@link SignalFreshnessSchema} (R10, KTD6).
   *
   * NULLABLE, and the null means one thing only: the READER did not derive a
   * signal for this row, which today is exactly the degraded row — one whose
   * registry columns could not be read, so nothing names which vendor lane it
   * even publishes on. Inventing a mode and a cadence for such a row would put
   * a judged-looking verdict on a row nobody could read. Null is NOT "fine",
   * and every surface renders it as an explicit unreported word (R17).
   */
  signal: SignalFreshnessSchema.nullable(),
  /**
   * True when the registry row could not be read as expected. Everything is
   * reported blocked and the row is named, rather than the read throwing —
   * one malformed row must never take the fleet response down with it.
   */
  degraded: z.boolean(),
  capabilities: z.array(AppReadinessCapabilityResultSchema),
  readyCapabilities: z.array(AppReadinessCapabilitySchema),
  blockedCapabilities: z.array(AppReadinessCapabilitySchema),
  unsupportedCapabilities: z.array(AppReadinessCapabilitySchema),
  notApplicableCapabilities: z.array(AppReadinessCapabilitySchema),
  /**
   * Deduped, sorted union of every missing `column` (not `field`) across
   * **blocked** capabilities — the operator's actionable list. `unsupported` and
   * `not-applicable` inputs are named on their own capability entry but stay
   * out of here: nothing an operator does to this app would clear them.
   */
  missingFields: z.array(z.string()),
  /**
   * The stored OneSignal channel snapshot both push capabilities were resolved
   * FROM, carried through so a surface can render the facts behind the verdict
   * (SCH-1512 U6).
   *
   * WHY IT IS ON THE PAYLOAD AT ALL. `push-ios` / `push-android` answer one
   * question — can this app reach that platform — and two things an operator
   * needs are deliberately NOT that question: the delivery-evidence advisory
   * (a channel that is configured and delivering nothing) and the legacy-GCM
   * advisory (a transport that works and is retired). KTD6 and KTD9 forbid
   * either from moving a capability, so neither can be derived from
   * `capabilities` at any zoom level, and a surface that only had the verdict
   * could not show them at all.
   *
   * ABSENT AND NULL BOTH MEAN "no snapshot travelled with this row", and every
   * reader must treat them the same way it treats a `stale_check` snapshot:
   * not checked, never ready. Null is what a degraded row carries and what a
   * reader that did not look sends; absent is a backend one deploy behind this
   * bundle. Neither is evidence about the app.
   *
   * This does NOT re-decide anything. `push-ios` / `push-android` are already
   * resolved in `capabilities` and stay the only verdict; these are the facts
   * they were read from.
   */
  pushChannel: MobileAppPushChannelSchema.nullish(),
  /**
   * How much of the school's self-service setup is filled (release lines U10,
   * R18). Present only for an app still in `PROVISIONING`, where it is what
   * staff check before provisioning; null or absent for every other app, and
   * for a row the reader could not judge.
   */
  setup: SchoolAppSetupCompletenessSchema.nullish(),
  /**
   * The next action from stored facts alone (SCH-2963): every app read carries it. The readiness
   * view (`GET /mobile-apps/:id/readiness`) sharpens it once the live gate has run. Optional only
   * because the backend and web deploy separately; absent means a backend one deploy behind.
   */
  nextAction: AppReadinessNextActionSchema.optional()
})
export type MobileAppReadiness = z.infer<typeof MobileAppReadinessSchema>

/** Pre-flight refusal payload — same data the UI badge renders, so they cannot disagree. */
export const AppReadinessRefusalSchema = z.object({
  appId: z.string(),
  appName: z.string().nullable(),
  capability: AppReadinessCapabilitySchema,
  status: AppReadinessStatusSchema,
  missing: z.array(AppReadinessMissingFieldSchema),
  message: z.string()
})
export type AppReadinessRefusal = z.infer<typeof AppReadinessRefusalSchema>
